<script setup lang="ts">
import CustomButton from "@/components/Button/Button.vue";
import {computed, ref} from "vue";
import {useWindowSize} from '@vueuse/core';
import {useField, useForm} from "vee-validate";
import * as yup from 'yup';
import {useUserStore} from "@/stores/userStore";
import {getUsers} from "@/api";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import AlternativeLogins from "@/components/LoginForm/AlternativeLogins.vue";

const {t} = useI18n();
const userStore = useUserStore();
const router = useRouter();
const route = useRoute();

const showPassword = ref(false);

defineEmits(['show-register', 'forgot-password', 'login-to-site'])

const query = route.query;

const schema = yup.object({
  email: yup.string().email().required(),
  password: yup.string().required(),
});
const form = useForm({validationSchema: schema});
const email = useField('email', schema);
const password = useField('password', schema);

const submit = form.handleSubmit(values => {
  getUsers().then(users => {
    const foundUser = users.data.find(user => user.email === values.email);

    if (foundUser !== undefined) {
      userStore.login(foundUser);
      router.push('/');
    } else {
      alert(t('invalid_email_or_password'))
    }
  })
});
</script>

<template>
  <div class="login-container w-1/2">
    <h1 class="form-title__text responsive-title-4 font-semibold self-start">{{ t('login_to_cosmos') }}</h1>
    <form class="pt-4" @submit.prevent="submit">
      <div class="w-full flex flex-col gap-y-4 ">
        <VTextField v-model="email.value.value" :label="t('username')" variant="underlined"
                    :error-messages="email.errorMessage.value"></VTextField>
        <VTextField v-model="password.value.value" :label="t('password')" variant="underlined"
                    :type="showPassword ? 'text' : 'password'">
          <template v-slot:append-inner>
            <div class="w-6 cursor-pointer" @click="showPassword = !showPassword" v-if="showPassword">
              <img src="@/assets/icons/svg/Hide_24x24.svg" :alt="t('hide')" width="64">
            </div>
            <div class="w-6 cursor-pointer" @click="showPassword = !showPassword" v-else>
              <img src="@/assets/icons/svg/Show_24x24.svg" :alt="t('show')" width="64">
            </div>
          </template>
        </VTextField>
        <div class="form-show-pass__text  responsive-body-medium text-end underline cursor-pointer font-semibold transition-opacity hover:opacity-60"
             @click="$emit('forgot-password')">
          {{ t('forgot_password') }}
        </div>
      </div>
      <CustomButton @click="$emit('login-to-site')" size="md-large" type="black" :rounded="true"
                    class="form-login__btn w-[15vw]  responsive-body-large mt-3 font-semibold">{{ t('login') }}
      </CustomButton>
    </form>
    <div class="no-account__text font-semibold text-md mt-6">{{ t('no_account') }} <span
        class="underline cursor-pointer ml-2 hover:opacity-60 transition-opacity"
        @click="$emit('show-register')">{{ t('sign_up') }}</span>
    </div>
    <AlternativeLogins/>

    <div class="mt-6 bg-red-200 px-4 py-3 rounded-6xl" v-if="query.boatId">
      You'll need to login in order to save your offers.
    </div>
  </div>

</template>

<style scoped>
:deep(.v-input__details) {
  display: none;
}

:deep(.v-input__details:has(.v-messages__message)) {
  display: block;
}

.form-login__btn {
  font-size: 1rem !important;
}
@media (max-width: 1538px) {
  .form-title__text {
    font-size: 1.5rem;
  }
  .form-show-pass__text {
    font-size: .875rem;
  }
}
@media (max-width: 1385px) {
  .login-container {
    width: 65%;
  }
  .form-login__btn {
    font-size: 1rem;
  }
}
@media (max-width: 1024px) {
  .login-container {
    width: 60%;
  }
  .form-login__btn {
  width: 18vw;
  }
  .form-title__text {
    font-size: 1.5rem;
  }
  .form-show-pass__text,
  .no-account__text{
    font-size: .875rem;
  }

}
@media (max-width: 768px) {
  .btn {
    font-size: 14px;
  }
  .login-container {
    width: 80%;
  }
  .form-login__btn {
    width: 100%;
    font-size: .875rem;
  }
  .form-title__text {
    font-size: 1.25rem;
  }

  .form-show-pass__text,
  .no-account__text {
    font-size: .75rem;
  }

}
</style>
